import {
  useUpdateWorkflowConfiguration,
  useGetAllActivities,
  useGetWorkflowDetail,
  useTrainSequenceModel
} from 'api/sdk';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useEffect, useMemo, useState } from 'react';
import { Workflow, WorkflowDependencyItem } from 'schemas';

export const useWorkflow = (mode?: string) => {
  const id = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const workflowId: any = queryParams.get('workflowId');

  const currentConfigId = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const { refetch: workflowRefetch }: any = useGetWorkflowDetail(
    currentConfigId,
    {
      workflow_id: workflowId
    }
  );
  const [selectedCameraID, setSelectedCameraID] = useState<string | null>();
  const [nextCameraID, setNextCameraID] = useState<string | null>();
  const [rows, setRows] = useState<any>();
  const [activeId, setActiveId] = useState<string | null>(null);
  const cameras = useSelector((state: RootState) => state.configV2.cameras);

  const { data: allActivities } = useGetAllActivities(id || '', {
    pipeline_satus: true || null
  });

  const activities = useMemo(() => {
    if (allActivities?.data.activity_details) {
      return allActivities?.data.activity_details;
    }
    return [];
  }, [allActivities]);

  const { refetch } = useGetWorkflowDetail(id || '');
  useEffect(() => {
    if (id && !mode) {
      refetch().then((response: any) => {
        if (response.data?.data) {
          if (response?.data?.data?.workflow) {
            setRows(response?.data?.data?.workflow);
          } else {
            setRows(response.data.data);
          }
        }
      });
    }

    if (workflowId && mode !== 'new') {
      workflowRefetch().then((response: any) => {
        setRows({ data: response?.data?.data?.data?.workflow });
      });
    }
  }, [id, workflowId]);

  const { mutateAsync, isLoading } = useUpdateWorkflowConfiguration();
  const {
    mutateAsync: train,
    isLoading: trainLoading,
    error: trainError
  } = useTrainSequenceModel();

  const addActivity = (data: Workflow) => {
    setRows((prevRows: any) => {
      // Create a deep copy of prevRows to avoid mutation
      const newRows = {
        configuration_id: id || '',
        data: Array.isArray(prevRows?.data) ? [...prevRows.data] : [],
        objectMapping: Array.isArray(prevRows?.objectMapping)
          ? [...prevRows.objectMapping]
          : [],
        objectWorkflow: Array.isArray(prevRows?.objectWorkflow)
          ? [...prevRows.objectWorkflow]
          : []
      };

      // Add the new data
      newRows.data.push(data);

      return newRows;
    });
  };

  const handleChangeDependency = (
    id: string,
    dependency: WorkflowDependencyItem[]
  ) => {
    setRows((prevRows: any) => {
      return {
        configuration_id: id,
        data:
          prevRows?.data.map((workflow: any) => {
            if (workflow.id === id) {
              return {
                ...workflow,
                dependency
              };
            }
            return workflow;
          }) || [],
        objectMapping: [],
        objectWorkflow: []
      };
    });
  };

  const handleSort = (
    active: string,
    over: string,
    selectedCameraID: string
  ) => {
    setRows((items: any) => {
      const newItems = [...(items?.data || [])];
      const activeIndex = newItems.findIndex(item => item?.id === active);
      const overIndex = newItems.findIndex(item => item?.id === over);
      const activeItem = newItems[activeIndex];
      newItems.splice(activeIndex, 1);
      newItems.splice(overIndex, 0, activeItem);
      return {
        configuration_id: id || '',
        data: newItems,
        objectMapping: [],
        objectWorkflow: []
      };
    });
  };

  const handleSave = async (): Promise<void> => {
    await mutateAsync({
      configId: id || '', // path parameter
      workflowId: workflowId, // path parameter
      data: {
        workflow_config: rows?.data || [] // request body
      }
    });
    return;
  };

  return {
    activities,
    handleSave,
    selectedCameraID,
    cameras,
    id,
    nextCameraID,
    setNextCameraID,
    setSelectedCameraID,
    rows,
    setActiveId,
    activeId,
    addActivity,
    setRows,
    handleSort,
    handleChangeDependency,
    isLoading,
    train,
    trainLoading,
    trainError
  };
};
